import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";

const MODULO = "Regionales";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const RegistrarRegionales = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [codRegional, setCodRegional] = useState("");
  const [nombreRegional, setNombreRegional] = useState("");

  const [loading, setLoading] = useState(false);

  const isEdit = useMemo(() => Boolean(id), [id]);

  // ==============================
  // CONFIG TOKEN
  // ==============================
  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  // ==============================
  // PERMISOS MÓDULO REGIONALES
  // ==============================
  const getModuloRegionales = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo ||
            modulo?.modulo ||
            modulo?.nombre ||
            "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloRegionales = useMemo(
    () => getModuloRegionales(),
    []
  );

  const puedeCrear =
    moduloRegionales?.puedeCrear === true ||
    moduloRegionales?.crear === true ||
    moduloRegionales?.create === true;

  const puedeActualizar =
    moduloRegionales?.puedeActualizar === true ||
    moduloRegionales?.actualizar === true ||
    moduloRegionales?.editar === true ||
    moduloRegionales?.update === true;

  // ==============================
  // VALIDAR SESIÓN
  // ==============================
  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");

          navigate("/", {
            replace: true,
          });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  // ==============================
  // LIMPIAR
  // ==============================
  const cleanData = () => {
    setCodRegional("");
    setNombreRegional("");
  };

  // ==============================
  // OBTENER DATOS
  // ==============================
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `/api/regionales/listid/${id}`,
        authConfig()
      );

      if (response.data.ok) {
        setCodRegional(
          response.data.regional.codRegional ?? ""
        );

        setNombreRegional(
          response.data.regional.nombreRegional ?? ""
        );
      }
    } catch (error) {
      console.error(
        "Error al obtener regional:",
        error.message
      );

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        "No se pudo cargar la regional",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    if (id) {
      getData();
    }
  }, [id, getData]);

  // ==============================
  // GUARDAR
  // ==============================
  const saveRegional = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para registrar regionales.",
        "warning"
      );

      return;
    }

    try {
      setLoading(true);

      const newRegional = {
        codRegional,
        nombreRegional,
      };

      await axios.post(
        "/api/regionales/add",
        newRegional,
        authConfig()
      );

      cleanData();

      Swal.fire({
        icon: "success",
        title:
          "La Regional ha sido registrada con éxito",
        showConfirmButton: false,
        timer: 1500,
      }).then(() =>
        navigate("/institucional/regional/")
      );
    } catch (error) {
      if (error.response?.data?.message) {
        Swal.fire(
          "Error",
          error.response.data.message,
          "error"
        );

        return;
      }

      console.error(
        "Error en saveRegional:",
        error.message
      );

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        "No se pudo registrar la regional.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // ACTUALIZAR
  // ==============================
  const updateRegional = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar regionales.",
        "warning"
      );

      return;
    }

    try {
      setLoading(true);

      const updatedRegional = {
        codRegional,
        nombreRegional,
      };

      const { data } = await axios.put(
        `/api/regionales/update/${id}`,
        updatedRegional,
        authConfig()
      );

      cleanData();

      Swal.fire({
        icon: "success",
        title:
          data.message ||
          "Regional actualizada con éxito",
        showConfirmButton: false,
        timer: 1500,
      }).then(() =>
        navigate("/institucional/regional/")
      );
    } catch (error) {
      if (error.response?.data?.message) {
        Swal.fire(
          "Error",
          error.response.data.message,
          "error"
        );

        return;
      }

      console.error(
        "Error al actualizar regional:",
        error.message
      );

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        "No se pudo actualizar la regional.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ==============================
  // ACCIONES
  // ==============================
  const actions = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (
      !codRegional ||
      !nombreRegional.trim()
    ) {
      Swal.fire(
        "Atención",
        "Completa todos los campos.",
        "warning"
      );

      return;
    }

    isEdit
      ? await updateRegional()
      : await saveRegional();
  };

  // ==============================
  // CANCELAR
  // ==============================
  const handleCancel = () => {
    cleanData();

    navigate("/institucional/regional/");
  };

  // ==============================
  // RENDER
  // ==============================
  return (
    <div className="bg-body-tertiary py-4">

      <div className="container-fluid px-2 px-md-4">

        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* =========================
              HEADER
          ========================= */}

          <div className="px-3 px-md-4 py-3 border-bottom bg-white">

            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              <div>
                <h5 className="mt-2 mb-1 fw-semibold">

                  {isEdit
                    ? "Editar Regional"
                    : "Registrar Regional"}

                </h5>

                <small className="text-secondary">

                  Completa la información para{" "}

                  {isEdit
                    ? "actualizar"
                    : "crear"}{" "}

                  una regional.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">

                <button
                  className="btn btn-outline-dark"
                  type="button"
                  onClick={() =>
                    navigate(
                      "/institucional/regional/"
                    )
                  }
                  title="Volver"
                  disabled={loading}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          {/* =========================
              BODY
          ========================= */}

          <div className="p-3 p-md-4">

            <form onSubmit={actions}>

              <div className="row g-3 justify-content-center">

                {/* =========================
                    CÓDIGO
                ========================= */}

                <div className="col-12 col-md-5">

                  <label
                    htmlFor="codigo"
                    className="form-label fw-semibold"
                  >
                    Código{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </label>

                  <div className="input-group">

                    <span className="input-group-text bg-white">
                      <i className="fa-solid fa-hashtag"></i>
                    </span>

                    <input
                      type="text"
                      className="form-control text-uppercase"
                      id="codigo"
                      value={codRegional}
                      onChange={(e) =>
                        setCodRegional(
                          e.target.value
                        )
                      }
                      placeholder="Ej: 05"
                      maxLength={10}
                      required
                      disabled={loading}
                    />
                  </div>

                  <small className="text-secondary">
                    Usa un código corto y único.
                  </small>
                </div>

                {/* =========================
                    REGIONAL
                ========================= */}

                <div className="col-12 col-md-5">

                  <label
                    htmlFor="nombreregional"
                    className="form-label fw-semibold"
                  >
                    Regional{" "}
                    <span className="text-danger">
                      *
                    </span>
                  </label>

                  <div className="input-group">

                    <span className="input-group-text bg-white">
                      <i className="fa-solid fa-building"></i>
                    </span>

                    <input
                      type="text"
                      className="form-control"
                      id="nombreregional"
                      value={nombreRegional}
                      onChange={(e) =>
                        setNombreRegional(
                          e.target.value
                        )
                      }
                      placeholder="Ej: Regional Antioquia"
                      maxLength={80}
                      required
                      disabled={loading}
                    />
                  </div>

                  <small className="text-secondary">
                    Escribe el nombre completo de la regional.
                  </small>
                </div>

                {/* =========================
                    BOTONES
                ========================= */}

                <div className="col-12 mt-3">

                  <div className="d-flex flex-column flex-md-row gap-2 justify-content-end">

                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    {((!isEdit && puedeCrear) ||
                      (isEdit &&
                        puedeActualizar)) && (
                      <button
                        type="submit"
                        className="btn btn-dark"
                        disabled={loading}
                        title={
                          isEdit
                            ? "Actualizar"
                            : "Guardar"
                        }
                      >
                        <i
                          className={`fa-solid ${
                            loading
                              ? "fa-spinner fa-spin"
                              : "fa-floppy-disk"
                          } me-2`}
                        ></i>

                        {loading
                          ? isEdit
                            ? "Actualizando…"
                            : "Guardando…"
                          : isEdit
                          ? "Actualizar"
                          : "Guardar"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {/* =========================
                LOADING EDIT
            ========================= */}

            {loading && isEdit && (
              <div className="text-center text-secondary py-4">

                <div
                  className="spinner-border"
                  role="status"
                />

                <div className="mt-3">
                  Cargando regional…
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarRegionales;